import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
// import { IconPresentationProps } from "@insite/mobius/Icon"; // Valtech Custom Code
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
// import { css } from "styled-components"; // Valtech Custom Code

/* Valtech Custom Code */
import translate from "@insite/client-framework/Translate";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { breakpointMediaQueries, resolveColor } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { BannerFieldDefinitions, BannerFields } from "@store/Overrides/Widgets/Basic/Banner";
import { transitionControlStyles, transitionHoverStyles } from "@store/theme/mixins";
import { bannerSpacingStyles } from "@store/theme/mixins/banner";
import { dotWrapperStyles, getCarouselArrowProps, getDotButtonProps } from "@store/theme/props/carousel";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import styled, { css } from "styled-components";

const Banner = getWidgetComponent("Basic/Banner");
const StyledArrowButton = styled(Button)`
    opacity: 0;
    transition: ${transitionHoverStyles()}, ${transitionControlStyles("opacity")};

    &:focus {
        opacity: 1;
    }
`;
/* Valtech Custom Code */

interface SlideModel {
    fields: BannerFields; // Valtech Custom Code
}

/*
    Valtech Removed Fields
    ----------------------------------
    height
    textAlignment
    responsiveFontSizes
    customFontSizes
    h1FontSize
    h2FontSize
    h3FontSize
    h4FontSize
    h5FontSize
    h6FontSize
    normalFontSize
    customCSS
*/

const enum fields {
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.slides]: SlideModel[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    // Valtech Custom Code
    dotButtonSelected?: ButtonPresentationProps;
}

/* Valtech Custom Code */
const arrowWrapperStyles = () => {
    return css`
        position: absolute;
        z-index: 1;
        top: 50%;
    `;
};
/* Valtech Custom Code */

export const slideshowStyles: SlideshowStyles = {
    slideshowWrapper: {
        css: css`
            position: relative;

            &:hover ${StyledArrowButton} {
                opacity: 1;
            }
        `,
    },
    prevArrowWrapper: {
        css: css`
            ${arrowWrapperStyles()}
            left: 0;
            transform: translate3d(50%, -300%, 0);

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            transform: translate3d(50%, -50%, 0);
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    prevArrowButton: getCarouselArrowProps("left"),
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;
        `,
    },
    slideContentWrapper: {
        css: css`
            position: relative;
            flex: 0 0 100%;
            display: flex;

            > div {
                flex: 0 0 100%;
            }
        `,
    },
    dotsContainerWrapper: {
        css: css`
            ${dotWrapperStyles()}
            margin: 20px 0;
            color: ${getColor("text")};

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            position: absolute;
                            bottom: 0;
                            left: 50%;
                            transform: translateX(-50%);
                            color: inherit;
                        `,
                        css`
                            margin: 25px 0;
                        `,
                        css`
                            margin: 30px 0;
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    dotButton: getDotButtonProps(),
    dotButtonSelected: getDotButtonProps(true),
    nextArrowWrapper: {
        css: css`
            ${arrowWrapperStyles()}
            right: 0;
            transform: translate3d(-50%, -300%, 0);

            ${props =>
                breakpointMediaQueries(
                    props.theme,
                    [
                        null,
                        null,
                        css`
                            transform: translate3d(-50%, -50%, 0);
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    nextArrowButton: getCarouselArrowProps("right"),
};

const defaultCustomCss = `.slideshow-wrapper {
}

.slideshow-carousel {
}

.slideshow-slide-container {
}

.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}

.slide-indicator {
}

.slide-dot-button {
}

.slide-arrow-wrapper {
}

.slide-arrow {
}
`;

export const Slideshow = ({ fields, extendedStyles }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, fields.autoplay * 1000);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            loop: true,
        });
    }, [embla, fields.slides]);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    /*
        Valtech Removed
        ------------------------
        heightStyles
        textAlignStyles
        customCSS
    */

    const slideshowWrapperStyles = {
        css: css`
            color: ${({ theme }) => {
                return resolveColor(fields.slides[selectedIndex]?.fields.invertColor ? "text.contrast" : "text", theme);
            }};
            ${styles.slideshowWrapper?.css}
        `,
    };

    return (
        <>
            {/* Valtech Custom Code */}
            <StyledWrapper {...slideshowWrapperStyles /* Valtech Custom Code */}>
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <StyledArrowButton {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                            <VisuallyHidden>{translate("Previous")}</VisuallyHidden> {/* Valtech Custom Code */}
                        </StyledArrowButton>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper {...styles.slideContainerWrapper}>
                        {fields.slides.map((slide, index) => {
                            /*
                                Valtech Removed
                                ------------------------
                                buttonLink
                                responsiveImageBehaviorStyles
                                backgroundStyles
                                overlayPositioningStyles
                                fontSizeStyles
                                slideWrapperStyles
                                slideOverlayWrapperStyles
                            */

                            /* Valtech Custom Code */
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper key={index} {...styles.slideContentWrapper}>
                                    <Banner
                                        fields={slide.fields}
                                        hideMargin={true}
                                        tabIndex={index === selectedIndex ? 0 : -1}
                                    />
                                </StyledWrapper>
                            );
                            /* Valtech Custom Code */
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {fields.showArrows && fields.slides.length > 1 && (
                    <StyledWrapper {...styles.nextArrowWrapper}>
                        <StyledArrowButton {...styles.nextArrowButton} onClick={() => embla && embla.scrollNext()}>
                            <VisuallyHidden>{translate("Next")}</VisuallyHidden> {/* Valtech Custom Code */}
                        </StyledArrowButton>
                    </StyledWrapper>
                )}
                {fields.slideIndicator && (
                    <StyledWrapper className="slide-indicator" {...styles.dotsContainerWrapper}>
                        {fields.slides.map((_, i) => {
                            /* Valtech Custom Code */
                            return (
                                <Button
                                    className="slide-dot-button"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={i}
                                    {...(i === selectedIndex ? styles.dotButtonSelected : styles.dotButton)}
                                    onClick={() => embla && embla.scrollTo(i)}
                                />
                            );
                            /* Valtech Custom Code */
                        })}
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {/* Valtech Custom Code */}
        </>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 0, // Valtech Custom Code
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: BannerFieldDefinitions.map(({ fieldType, ...props }) => props), // Valtech Custom Code
            },
        ],
    },
};

export default widgetModule;
